import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import type {
  ClientEngagementDocument,
  ServiceDocument,
  BoardTemplateDocument,
} from '@/types'
import { projectBoardsService } from '../services/projectBoards.service'
import { boardTemplatesService } from '../services/boardTemplates.service'

type Props = {
  open: boolean
  engagement: ClientEngagementDocument & { id: string }
  onClose: () => void
}

const AUTO = '__auto'
const BLANK = '__blank'

type LoadedTemplate = BoardTemplateDocument & { id: string }

export function CreateBoardDialog({ open, engagement, onClose }: Props) {
  const navigate = useNavigate()

  const serviceIds = useMemo(() => {
    const ids = (engagement.lineItems ?? [])
      .map((li) => li.sourceServiceId)
      .filter((id): id is string => Boolean(id))
    return Array.from(new Set(ids))
  }, [engagement.lineItems])

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [templates, setTemplates] = useState<LoadedTemplate[]>([])
  const [services, setServices] = useState<Record<string, ServiceDocument | null>>({})
  const [selections, setSelections] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const [tpls, svcEntries] = await Promise.all([
          boardTemplatesService.list(),
          Promise.all(
            serviceIds.map(async (sid) => {
              const svc = await getDocument(typedDoc<ServiceDocument>('services', sid))
              return [sid, svc] as const
            })
          ),
        ])
        if (cancelled) return
        setTemplates(tpls)
        const svcMap: Record<string, ServiceDocument | null> = {}
        for (const [sid, svc] of svcEntries) svcMap[sid] = svc
        setServices(svcMap)
        const initial: Record<string, string> = {}
        for (const sid of serviceIds) initial[sid] = AUTO
        setSelections(initial)
      } catch (err) {
        if (!cancelled) alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, serviceIds])

  function autoMatch(serviceId: string): LoadedTemplate | undefined {
    return templates.find(
      (t) => !t.isArchived && t.defaultForServiceIds?.includes(serviceId)
    )
  }

  async function handleCreate() {
    setSubmitting(true)
    try {
      const overrides: Record<string, string> = {}
      for (const sid of serviceIds) {
        const sel = selections[sid] ?? AUTO
        if (sel !== AUTO) overrides[sid] = sel
      }
      await projectBoardsService.createRootBoard(
        engagement.id,
        Object.keys(overrides).length > 0 ? overrides : undefined
      )
      navigate('/admin/client-services/engagements/' + engagement.id + '/board')
      onClose()
    } catch (err) {
      alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create project board for {engagement.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading templates…</p>
          ) : serviceIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This engagement has no services. The board will start blank.
            </p>
          ) : (
            serviceIds.map((sid) => {
              const svc = services[sid]
              const sel = selections[sid] ?? AUTO
              const match = autoMatch(sid)
              return (
                <div key={sid} className="space-y-1.5">
                  <Label>{svc?.title ?? '(Unknown service)'}</Label>
                  <Select
                    value={sel}
                    onValueChange={(v) =>
                      setSelections((prev) => ({ ...prev, [sid]: v }))
                    }
                    disabled={submitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AUTO}>Auto-pick default template</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                      <SelectItem value={BLANK}>Blank — no template</SelectItem>
                    </SelectContent>
                  </Select>
                  {sel === AUTO && (
                    <p className="text-xs text-muted-foreground">
                      Will use:{' '}
                      {match ? match.name : '(no matching default — blank)'}
                    </p>
                  )}
                </div>
              )
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || submitting}>
            {submitting
              ? 'Creating…'
              : serviceIds.length === 0
                ? 'Create blank board'
                : 'Create board'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
